import React, { FunctionComponent, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Candidate, ModelVariant, SupportType } from '../../api';
import { Anchor, Question } from '../../App.styles';
import { Button, NavFooter } from '../../components';
import NotificationBox, { Text } from '../../components/NotificationBox';
import Result from '../../components/Result/Result';
import { Disclaimer, ResultInfo } from '../../components/Result/Result.styles';
import RingtercomCards from '../../components/RingtercomCards';
import { RingtercomCard } from '../../components/RingtercomCards/RingtercomCards';
import { BottomBorder } from '../../components/Stepper/Stepper.styles';
import {
  CandidateModelsContext,
  FlowStep,
  GlobalContext,
  StepperContext,
  updateFlowStep,
} from '../../contexts';
import { NEUTRAL_500, PRIMARY_500 } from '../../styles/colors';
import { StepKey } from '../../types';
import {
  ALWAYS_VISIBLE,
  closeAmzModal,
  formatCurrency,
  iconUrl,
  imagesUrl,
  isAmazonHost,
  RingtercomFeature,
  RingtercomVersion,
  sessionStorageUtils,
  TestId,
  visibleExceptForCountries,
} from '../../utils';
import { environment } from '../../utils/environment';
import { Treatment } from '../../weblab/treatment';
import {
  Coupon,
  CouponDisclaimer,
  Icon,
  ICON_TYPES,
  ResultQuestion,
  RingToOpen,
} from './Success.styles';

const SuccessVideoSupport: FunctionComponent = () => {
  const { dispatch } = React.useContext(GlobalContext);
  const { data } = React.useContext(CandidateModelsContext);
  const { stepperData, updateStepperData, steps } =
    React.useContext(StepperContext);
  const {
    brand,
    model,
    variant: stepperVariant,
    secondAttempt,
    candidate,
  } = stepperData;
  const { t } = useTranslation('common');
  const { coupon, treatment } = environment;
  const candidates = data?.candidates;

  const variant = stepperVariant || model?.variants[0];
  let supportByHwVersion:
    | {
        [key in RingtercomVersion]: { [key in RingtercomFeature]: SupportType };
      } = Object.values(RingtercomVersion).reduce(
    (acc, version) => {
      acc[version] = {
        [RingtercomFeature.AUDIO]: 'no_support',
        [RingtercomFeature.VIDEO]: 'no_support',
      };
      return acc;
    },
    {} as {
      [key in RingtercomVersion]: { [key in RingtercomFeature]: SupportType };
    }
  );

  const updateSupportForHwVersion = (item: Candidate | ModelVariant) => {
    const { hw_version, has_video } = item;
    if (!hw_version) {
      return;
    }
    supportByHwVersion[hw_version][RingtercomFeature.AUDIO] = 'full_support';
    supportByHwVersion[hw_version][RingtercomFeature.VIDEO] = has_video
      ? 'full_support'
      : 'no_support';
  };

  if (candidate) {
    candidates
      ?.filter((c) => {
        const labels = candidate?.terminal_labels?.join('');

        return (
          c.support === 'full_support' && labels === c.terminal_labels.join('')
        );
      })
      .forEach(updateSupportForHwVersion);
  } else if (model) {
    model?.variants
      .filter(
        (v) =>
          v.support === 'full_support' &&
          v.backplate_number === variant?.backplate_number
      )
      .forEach(updateSupportForHwVersion);
  } else {
    if (data?.support_by_hw_version) {
      supportByHwVersion = data.support_by_hw_version;
    }
  }

  useEffect(() => {
    if (isAmazonHost()) {
      return;
    }
    sessionStorageUtils.save('stepper_context_data', stepperData);
    sessionStorageUtils.save('stepper_context_steps', steps);
    sessionStorageUtils.save('candidate_models_data', data);
  }, [stepperData, steps, data]);

  const hedgeCompatibility =
    supportByHwVersion?.[RingtercomVersion.V2]?.[RingtercomFeature.AUDIO] ===
    'full_support';
  const hasVideo =
    supportByHwVersion?.[RingtercomVersion.V2]?.[RingtercomFeature.VIDEO] ===
    'full_support';
  const forgeCompatibility = Object.entries(supportByHwVersion || {})
    .filter(([k]) => k !== RingtercomVersion.V2)
    .some(([, v]) => v[RingtercomFeature.AUDIO] === 'full_support');

  const videoIsNotSupportedBox = (
    <NotificationBox variant="info">
      <Text color={NEUTRAL_500}>
        {t('results.success.ringtercomCard.videoIsNotSupportedBox')}
      </Text>
    </NotificationBox>
  );

  const ringtercomCardsData: RingtercomCard[] = [
    {
      tag: <></>,
      deviceType: RingtercomFeature.VIDEO,
      compatible: hedgeCompatibility,
      videoSupport: hasVideo,
      deviceName: t('results.success.ringtercomCard.titleVideo'),
      deviceDescription: t('results.success.ringtercomCard.videoDescription'),
      deviceIcon: iconUrl.personDetectionOutline,
      isVisible: ALWAYS_VISIBLE,
      features: [
        <>
          <img alt="" src={iconUrl.supportVideo}></img>
          <span>
            <Trans
              i18nKey="common:results.success.ringtercomCard.remoteVideoFeature"
              components={{
                bold: <span className="fw-bold" />,
              }}
            />
          </span>
        </>,
        <>
          <img alt="" src={iconUrl.amazonKeyAccess}></img>
          <span>{t('results.success.ringtercomCard.autoVerifyFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.sharedAlerts}></img>
          <span>{t('results.success.ringtercomCard.shareKeysFeature')}</span>
        </>,
      ],
      shopUrl: t(
        `results.success.ringtercomCard.shopNowBtnUrl.${
          isAmazonHost() ? 'amazon' : 'ring'
        }.video`
      ),
      children: (
        <>
          {hedgeCompatibility &&
            !hasVideo &&
            !forgeCompatibility &&
            videoIsNotSupportedBox}
        </>
      ),
    },
    {
      tag: <></>,
      deviceType: RingtercomFeature.AUDIO,
      compatible: forgeCompatibility,
      videoSupport: false,
      deviceName: t('results.success.ringtercomCard.titleAudio'),
      deviceDescription: t('results.success.ringtercomCard.audioDescription'),
      deviceIcon: iconUrl.audioIntercom,
      isVisible: visibleExceptForCountries(environment.hideProductCardsForCountries),
      features: [
        <>
          <img alt="" src={iconUrl.generalAudio}></img>
          <span>{t('results.success.ringtercomCard.remoteAudioFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.amazonKeyAccess}></img>
          <span>{t('results.success.ringtercomCard.autoVerifyFeature')}</span>
        </>,
        <>
          <img alt="" src={iconUrl.sharedAlerts}></img>
          <span>{t('results.success.ringtercomCard.shareKeysFeature')}</span>
        </>,
      ],
      shopUrl: t(
        `results.success.ringtercomCard.shopNowBtnUrl.${
          isAmazonHost() ? 'amazon' : 'ring'
        }.audio`
      ),
      children: (
        <>
          {forgeCompatibility && !hedgeCompatibility && videoIsNotSupportedBox}
        </>
      ),
    },
  ].sort((x, y) => {
    if (x.compatible !== y.compatible) {
      return y.compatible ? 1 : -1;
    }
    if (hasVideo) {
      return x.deviceType === RingtercomFeature.VIDEO ? -1 : 1;
    }
    return x.deviceType === RingtercomFeature.AUDIO ? -1 : 1;
  });

  if (forgeCompatibility && hedgeCompatibility) {
    ringtercomCardsData[0].tag = (
      <div className="tag recommended">{t('compatibilityOptions.tag')}</div>
    );
    ringtercomCardsData
      .slice(1)
      .forEach(
        (card) =>
          (card.tag = (
            <div className="tag no-video">
              {card.deviceType === RingtercomFeature.VIDEO && !card.videoSupport
                ? t('results.success.ringtercomCard.audioOnlyTag')
                : t('results.success.ringtercomCard.noVideoTag')}
            </div>
          ))
      );
  } else {
    ringtercomCardsData[0].tag = (
      <div className="tag recommended">
        {t('results.success.ringtercomCard.compatibleTag')}
      </div>
    );
    ringtercomCardsData
      .slice(1)
      .forEach(
        (card) =>
          (card.tag = (
            <div className="tag not-compatible">
              {t('images.notCompatible')}
            </div>
          ))
      );
  }

  const getCompatibilityResultText = (card: RingtercomCard) => {
    const prefix = card.deviceName;
    let sufix = t('results.success.compatibilityResults.notCompatibleSufix');
    if (card.compatible) {
      if (card.deviceType === RingtercomFeature.AUDIO) {
        sufix = hasVideo
          ? t(
              'results.success.compatibilityResults.audio.compatibleNoVideoSufix'
            )
          : hedgeCompatibility
          ? t('results.success.compatibilityResults.audio.compatibleSufix')
          : t(
              'results.success.compatibilityResults.audio.compatibleNoVideoSufix'
            );
      } else {
        sufix = hasVideo
          ? t('results.success.compatibilityResults.video.compatibleSufix')
          : t(
              'results.success.compatibilityResults.video.compatibleAudioOnlySufix'
            );
      }
    }
    return `${prefix}${sufix}`;
  };

  return (
    <Result
      image={{
        source: hasVideo ? imagesUrl.success_video : imagesUrl.success_audio,
        altText: t('images.success'),
        cssClasses: 'w-100 h-100 object-fit-cover',
      }}
      stepKey={StepKey.Success}
    >
      <div
        className="d-flex flex-column gap-3 h-100"
        data-testid={TestId.Success}
      >
        <div className="d-flex flex-column gap-3 flex-grow-1">
          <div className="d-flex flex-column gap-2">
            <ResultQuestion as={Question}>
              {`${t('results.success.titleCompatibilityResults')}${
                brand && model?.model_name
                  ? ' ' + brand + ' ' + model.model_name
                  : ''
              }`}
            </ResultQuestion>

            {ringtercomCardsData
              .filter((data) => !data.isVisible || data.isVisible())
              .map((card) => (
                <ResultQuestion
                  key={card.deviceType}
                  as={Question}
                  className="d-flex"
                >
                  <Icon
                    type={
                      card.compatible
                        ? isAmazonHost()
                          ? ICON_TYPES.SUCCESS_AMZ
                          : ICON_TYPES.SUCCESS
                        : isAmazonHost()
                        ? ICON_TYPES.NEGATIVE_AMZ
                        : ICON_TYPES.NEGATIVE
                    }
                    className="d-flex pe-3"
                  />
                  <span>{getCompatibilityResultText(card)}</span>
                </ResultQuestion>
              ))}
          </div>

          {hedgeCompatibility &&
            forgeCompatibility &&
            !hasVideo &&
            videoIsNotSupportedBox}

          {variant?.ring_to_open && (
            <RingToOpen>
              <span className={!isAmazonHost() ? 'fw-bold' : ''}>
                {t('results.success.r2oTitle')}
              </span>{' '}
              {t('results.success.r2oDescription')}
            </RingToOpen>
          )}

          {treatment === Treatment.V3_5 && !secondAttempt && (
            <ResultInfo className="mx-0 fw-semibold">
              {t('results.success.multiDoor')}{' '}
              <Anchor
                tabIndex={0}
                role="button"
                onClick={() => {
                  updateStepperData({
                    activeStep: steps.findIndex(
                      (s) => s === StepKey.IsParallelAfterSuccess
                    ),
                    multiDoor: true,
                    secondAttempt: true,
                  });
                  dispatch(
                    updateFlowStep({
                      flowStep: FlowStep.compatibilityChecker,
                    })
                  );
                }}
                data-testid={TestId.LinkContinue}
              >
                {t('buttons.continue')}
              </Anchor>
            </ResultInfo>
          )}

          {coupon?.code && coupon?.amount && coupon?.currency && (
            <Coupon className="border d-flex flex-column gap-2">
              <ResultQuestion as={Question}>
                <Trans
                  i18nKey="common:results.success.coupon.congrats"
                  values={{
                    code: coupon.code,
                    amount: formatCurrency(coupon.amount, coupon.currency),
                  }}
                  components={{
                    couponCode: <span style={{ color: PRIMARY_500 }} />,
                    amount: <span style={{ color: PRIMARY_500 }} />,
                  }}
                />
              </ResultQuestion>

              <CouponDisclaimer as={Disclaimer}>
                <Trans
                  i18nKey="common:results.success.coupon.disclaimer"
                  values={{
                    code: coupon.code,
                  }}
                  components={{
                    tncLink: (
                      <Anchor
                        tabIndex={0}
                        noUnderline
                        target="_blank"
                        href="/gp/help/customer/display.html?nodeId=G8LF2MC32ZASDWVU"
                        role="button"
                      />
                    ),
                  }}
                />
              </CouponDisclaimer>
            </Coupon>
          )}

          {!isAmazonHost() && <BottomBorder />}
          <RingtercomCards cards={ringtercomCardsData} />
        </div>

        {isAmazonHost() && (
          <NavFooter>
            <Button type="button" onClick={closeAmzModal}>
              {t('header.close')}
            </Button>
            <Button
              type="button"
              ghost
              onClick={() => {
                dispatch(
                  updateFlowStep({ flowStep: FlowStep.compatibilityChecker })
                );
              }}
            >
              {t('header.back')}
            </Button>
          </NavFooter>
        )}
      </div>
    </Result>
  );
};

export default SuccessVideoSupport;
